import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'

import {
  HouseAndLandInterface,
  setByFlag,
} from '@store/actionSlices/houseAndLand'
import { resetFilter, setFilter } from '@store/actionSlices/lotFilter'
import { RootStateType } from '@store/store'
import {
  HouseAndLandConfigurationInterface,
  LotFilterInterface,
  PriceRange,
  PropertyConfigurationInterface,
} from '@store/types'

import DropDown from '@components/dropdown'
import IdleTimeHandler from '@components/idle-time-handler'
import ToggleSwitch from '@components/toggle-switch'

import { HouseAndLandActiveKeyType } from '@api/types/house-and-land-type'
import { Filter } from '@api/white-label'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import { CloseSvg } from '@svg/react'

import { MinMaxInterface } from '@adUtilities/types/common'
import {
  FilterOptionLotConfigurations,
  FilterOptionsConfigurations,
} from '@adUtilities/types/houseAndLand'

import { DEFAULT_ANY_OPTION } from './constants'
import RangeOptions from './range-options'

interface ComponentProps {
  isOpen: boolean
  toggle: (arg: boolean) => void
  lotFilter: LotFilterInterface
  prices: PriceRange
  showPrice: boolean
  houseAndLand: HouseAndLandInterface
  aspects: Array<string>
  propertyTypeOptions: Array<string>
  propertyConfig: PropertyConfigurationInterface
  houseAndLandConfig: HouseAndLandConfigurationInterface
  hideStages?: boolean
  isFetchingStages?: boolean
  filterWhiteLabel: Filter
}
function HouseAndLandFilterPopup({
  isOpen,
  toggle,
  lotFilter,
  prices,
  showPrice = true,
  houseAndLand,
  propertyConfig,
  houseAndLandConfig,
  aspects,
  propertyTypeOptions,
  hideStages,
  isFetchingStages,
  filterWhiteLabel,
}: ComponentProps) {
  const filterRef = useRef<HTMLDivElement>(null)
  const precincts = houseAndLand.precinctList
  const {
    lotFrontage: lotFrontageOptions,
    lotArea: lotAreaOptions,
    storey: storeyOptions,
    builder: builderOptions,
    packageFilter,
  } = houseAndLandConfig || {}
  const dispatch = useDispatch()

  const [activeStage, setActiveStage] = useState<string>(
    houseAndLand.activeStageId
  )
  const [checkAnyStage, setAnyStage] = useState(lotFilter.anyStage)
  const [configurations, setConfigurations] =
    useState<FilterOptionsConfigurations>(lotFilter.configurations)
  const [lotConfigurations, setLotConfigurations] =
    useState<FilterOptionLotConfigurations>(lotFilter.lotConfiguration)
  const [builder, setBuilder] = useState<Array<string>>(lotFilter.builder)

  const [packages, setPackages] = useState<Array<string>>(lotFilter.packages)
  const [storey, setStorey] = useState<Array<number>>(lotFilter.storey)
  const [showAvailable, setShowAvailable] = useState(
    lotFilter.showOnlyAvailable
  )
  const packageSummary = houseAndLand.matrixData[houseAndLand.activePrecinctId]
  const precinctLength = useMemo(() => precincts.length, [precincts])

  const stageOptions = useMemo(
    () =>
      Object.keys(packageSummary?.stages ?? {})?.map((label) => ({
        label: `Stage ${label}`,
        value: label,
      })),
    [packageSummary]
  )

  const packageOptions = useMemo(
    () => packageSummary?.packages?.map((pkg) => pkg.name) ?? [],
    [packageSummary]
  )

  const handleBuilderOnChange = (value: string) => {
    if (builder.includes(value)) {
      dispatch(
        setFilter({
          ...lotFilter,
          builder: builder.filter((item) => item !== value),
        })
      )
    } else {
      dispatch(setFilter({ ...lotFilter, builder: [...builder, value] }))
    }
  }

  const handleStoreyOnChange = (value: number) => {
    if (storey.includes(value)) {
      dispatch(
        setFilter({
          ...lotFilter,
          storey: storey.filter((item) => item !== value),
        })
      )
    } else {
      dispatch(setFilter({ ...lotFilter, storey: [...storey, value] }))
    }
  }

  const handleAspectOnChange = (value: string) => {
    if (value === DEFAULT_ANY_OPTION) {
      dispatch(
        setFilter({
          ...lotFilter,
          lotConfiguration: { ...lotConfigurations, aspects: [] },
        })
      )
      return
    }

    const lotAspect = lotConfigurations?.aspects
    if (!lotAspect) return

    const updatedAspects = lotAspect.includes(value)
      ? lotAspect.filter((item) => item !== value)
      : [...lotAspect, value]

    dispatch(
      setFilter({
        ...lotFilter,
        lotConfiguration: { ...lotConfigurations, aspects: updatedAspects },
      })
    )
  }

  const handleTypeOnChange = (value: string) => {
    const { propertyTypes = [] } = lotConfigurations

    const updatedPropertyTypes = propertyTypes.includes(value)
      ? propertyTypes.filter((item) => item !== value)
      : [...propertyTypes, value]

    dispatch(
      setFilter({
        ...lotFilter,
        lotConfiguration: {
          ...lotConfigurations,
          propertyTypes: updatedPropertyTypes,
        },
      })
    )
  }

  const handlePackageOnChange = (value: string) => {
    const updatedPackages = packages.includes(value)
      ? packages.filter((item) => item !== value)
      : [...packages, value]

    dispatch(
      setFilter({
        ...lotFilter,
        packages: updatedPackages,
      })
    )
  }

  const handleConfigurationOnChange = (
    configs: FilterOptionsConfigurations
  ) => {
    dispatch(setFilter({ ...lotFilter, configurations: configs }))
  }
  const handleLotConfigurationOnChange = (
    configs: FilterOptionLotConfigurations
  ) => {
    dispatch(setFilter({ ...lotFilter, lotConfiguration: configs }))
  }

  const handleShowAvailable = (checked: boolean) =>
    dispatch(setFilter({ ...lotFilter, showOnlyAvailable: checked }))

  const handleStageOnChange = (value: string) => {
    dispatch(
      setByFlag({ flag: HouseAndLandActiveKeyType.ActiveLotId, value: '' })
    )
    dispatch(
      setByFlag({ flag: HouseAndLandActiveKeyType.ActiveStageId, value })
    )
  }

  const handleAnyStage = (checked: boolean) => {
    dispatch(setFilter({ ...lotFilter, anyStage: checked }))
  }

  const apply = () => {
    toggle(false)
    dispatch(setFilter({ ...lotFilter, apply: true }))
    if (precinctLength > 1) {
      dispatch(
        setByFlag({ flag: HouseAndLandActiveKeyType.ActiveLotId, value: '' })
      )
      if (stageOptions.length > 1) {
        dispatch(
          setByFlag({
            flag: HouseAndLandActiveKeyType.ActiveStageId,
            value: activeStage,
          })
        )
      }
    } else {
      handleStageOnChange(activeStage)
    }
  }

  const reset = () => {
    dispatch(resetFilter())
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      filterRef.current &&
      !filterRef.current.contains(event.target as Node)
    ) {
      if (isOpen) {
        toggle(false)
      }
    }
  }

  useEffect(() => {
    if (isOpen && lotFilter.apply) {
      dispatch(setFilter({ ...lotFilter, apply: false }))
    }
  }, [lotFilter, activeStage])

  useEffect(() => {
    setConfigurations(lotFilter.configurations)
    setLotConfigurations(lotFilter.lotConfiguration)
    setBuilder(lotFilter.builder)
    setStorey(lotFilter.storey)
    setShowAvailable(lotFilter.showOnlyAvailable)
    setAnyStage(lotFilter.anyStage)
    setPackages(lotFilter.packages)
  }, [lotFilter])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick, false)
    }, 0)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [isOpen])

  return (
    <IdleTimeHandler>
      <div
        ref={filterRef}
        className={`fixed top-[36px] z-30 h-page-container w-[297px] rounded-tl-2xl bg-white px-4 pb-10 text-black shadow-2xl ${
          isOpen ? '' : '-translate-x-full'
        } transition-transform duration-500`}
      >
        <div className="flex h-screen flex-col">
          <div className="mb-2 mt-6 flex items-center justify-between">
            <div>
              <button
                className="flex items-center"
                type="button"
                onClick={() => toggle(!isOpen)}
              >
                <CloseSvg
                  className="h-3.5 w-3.5 cursor-pointer"
                  strokeColor="black"
                />
              </button>
            </div>
            <div>
              <h4 className="text-xl font-semibold">Filter</h4>
            </div>
            <div>
              <button
                type="button"
                onClick={() => reset()}
                className="text-base font-medium text-mainColour underline"
              >
                Reset
              </button>
            </div>
          </div>
          <div className="no-scrollbar h-[84%] overflow-y-auto">
            <div className="mt-6 flex flex-col gap-[1.20rem] text-base font-medium">
              {!hideStages && isFetchingStages && (
                <div className="flex flex-col gap-1">
                  <div className="h-4 w-16 rounded-lg bg-slate-400/20">
                    <div className="shimmer-container relative !p-2 opacity-50"></div>
                  </div>
                  <div className="h-10 w-full rounded-lg bg-slate-400/20">
                    <div className="shimmer-container relative opacity-50"></div>
                  </div>
                  <div className="mt-2 h-4 w-32 rounded-lg bg-slate-400/20">
                    <div className="shimmer-container relative !p-2 opacity-50"></div>
                  </div>
                </div>
              )}

              {stageOptions?.length > 1 && !hideStages && !isFetchingStages && (
                <div className="flex flex-col gap-1">
                  <span className={` ${checkAnyStage ? 'text-gray-300' : ''}`}>
                    {filterWhiteLabel.stageDropdownLabel}
                  </span>
                  <DropDown
                    onSelect={(value) => {
                      setActiveStage(value)
                    }}
                    items={stageOptions}
                    disabled={checkAnyStage}
                    value={activeStage}
                    className="py-2"
                    buttonRadius="rounded-[7px]"
                    buttonRadiusClasses="rounded-tl-[7px] rounded-tr-[7px]"
                    optionRadiusClasses="rounded-bl-[7px] rounded-br-[7px]"
                  />

                  <div className="mt-2 inline-flex items-center">
                    <ToggleSwitch
                      key="anyLevel"
                      onChangeCallback={(e) => {
                        handleAnyStage(e.target.checked)
                      }}
                      initialState={checkAnyStage}
                      onColor="bg-mainColour"
                      offColor="bg-[#BDC3C7]"
                    />
                    <div className="ml-3 text-base font-medium">Any Stage</div>
                  </div>
                </div>
              )}

              {showPrice && lotConfigurations?.price && (
                <RangeOptions
                  field="price"
                  options={prices}
                  range={lotConfigurations.price}
                  handleChange={(configValue: MinMaxInterface) => {
                    handleLotConfigurationOnChange({
                      ...lotConfigurations,
                      price: configValue,
                    })
                  }}
                />
              )}

              {Object.entries(propertyConfig)?.map(([field, value]) => {
                const range =
                  configurations[field as keyof FilterOptionsConfigurations]
                return (
                  range &&
                  value?.length > 0 && (
                    <RangeOptions
                      field={field}
                      key={field}
                      options={value as unknown as Array<string | number>}
                      range={range}
                      handleChange={(configValue: MinMaxInterface) => {
                        handleConfigurationOnChange({
                          ...configurations,
                          [field as keyof FilterOptionsConfigurations]:
                            configValue,
                        })
                      }}
                    />
                  )
                )
              })}

              {lotFrontageOptions &&
                lotFrontageOptions?.length > 0 &&
                lotConfigurations.frontage && (
                  <RangeOptions
                    field="lotFrontage"
                    options={lotFrontageOptions}
                    range={lotConfigurations.frontage}
                    handleChange={(configValue: MinMaxInterface) => {
                      handleLotConfigurationOnChange({
                        ...lotConfigurations,
                        frontage: configValue,
                      })
                    }}
                  />
                )}

              {lotAreaOptions &&
                lotAreaOptions?.length > 0 &&
                lotConfigurations.area && (
                  <RangeOptions
                    field="lotArea"
                    options={lotAreaOptions}
                    range={lotConfigurations.area}
                    handleChange={(configValue: MinMaxInterface) => {
                      handleLotConfigurationOnChange({
                        ...lotConfigurations,
                        area: configValue,
                      })
                    }}
                  />
                )}

              {propertyTypeOptions?.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span>Type</span>
                  <div className="grid grid-cols-1 gap-2">
                    {propertyTypeOptions?.map((value) => {
                      const isPropertyTypeSelected =
                        lotConfigurations?.propertyTypes?.includes(
                          value.toLowerCase()
                        )

                      return (
                        <button
                          type="button"
                          key={value}
                          onClick={() => {
                            handleTypeOnChange(value.toLowerCase())
                          }}
                          className={`rounded py-2 text-center text-base font-medium ${
                            isPropertyTypeSelected
                              ? 'bg-mainColour text-white'
                              : 'bg-[#E5E5E5] hover:bg-[#BDC3C7]'
                          }`}
                        >
                          {value}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {storeyOptions && storeyOptions?.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span>Storey</span>
                  <div className="grid grid-cols-1 gap-2">
                    {storeyOptions?.map(({ label, value }) => (
                      <button
                        type="button"
                        key={label}
                        onClick={() => {
                          handleStoreyOnChange(value)
                        }}
                        className={`rounded py-2 text-center text-base font-medium ${
                          storey.includes(value)
                            ? 'bg-mainColour text-white'
                            : 'bg-[#E5E5E5] hover:bg-[#BDC3C7]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {aspects && aspects.length > 0 && lotConfigurations.aspects && (
                <div className="flex flex-col gap-1">
                  <span>Aspect</span>
                  <DropDown
                    onSelect={(item) => handleAspectOnChange(item)}
                    items={[
                      { value: DEFAULT_ANY_OPTION, label: DEFAULT_ANY_OPTION },
                      ...aspects.map((item) => ({
                        value: item,
                        label: item,
                      })),
                    ]}
                    value={
                      lotConfigurations?.aspects?.length === 0
                        ? DEFAULT_ANY_OPTION
                        : lotConfigurations?.aspects?.join(', ')
                    }
                    className="break-all py-2"
                    buttonRadius="rounded-[7px]"
                    buttonRadiusClasses="rounded-tl-[7px] rounded-tr-[7px]"
                    optionRadiusClasses="rounded-bl-[7px] rounded-br-[7px]"
                  />
                </div>
              )}

              {builderOptions && builderOptions?.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span>Builder</span>
                  <div className="grid grid-cols-1 gap-2">
                    {builderOptions?.map((builderValue: string) => (
                      <button
                        type="button"
                        key={builderValue}
                        onClick={() => {
                          handleBuilderOnChange(builderValue.toLowerCase())
                        }}
                        className={`rounded py-2 text-center text-base font-medium ${
                          builder.includes(builderValue.toLowerCase())
                            ? 'bg-mainColour text-white'
                            : 'bg-[#E5E5E5] hover:bg-[#BDC3C7]'
                        }`}
                      >
                        {builderValue}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {packageFilter && packageOptions?.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span>Packages</span>
                  <div className="grid grid-cols-1 gap-2">
                    {packageOptions?.map((value) => {
                      const isPackageSelected = packages?.includes(
                        value.toLowerCase()
                      )

                      return (
                        <button
                          type="button"
                          key={value}
                          onClick={() =>
                            handlePackageOnChange(value.toLowerCase())
                          }
                          className={`rounded py-2 text-center text-base font-medium ${
                            isPackageSelected
                              ? 'bg-mainColour text-white'
                              : 'bg-[#E5E5E5] hover:bg-[#BDC3C7]'
                          }`}
                        >
                          {value}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mb-12 flex flex-col gap-[1.20rem]">
            <div className="mt-2 inline-flex items-center whitespace-nowrap">
              <ToggleSwitch
                key="checkbox"
                onChangeCallback={(e) => {
                  handleShowAvailable(e.target.checked)
                }}
                initialState={showAvailable}
                onColor="bg-mainColour"
                offColor="bg-[#BDC3C7]"
              />
              <div className="ml-3 text-base font-medium">
                {filterWhiteLabel.toggleText}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={apply}
                disabled={lotFilter.apply}
                className={`rounded-lg px-4 py-4 text-xl font-semibold text-white duration-300 ${
                  lotFilter.apply ? 'bg-gray-300' : 'bg-mainColour'
                }`}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </IdleTimeHandler>
  )
}
export default connect(
  ({
    projectIdentity: { projectName },
    lotFilter,
    projectConfig: {
      prices,
      showPrice,
      propertyConfig,
      aspects,
      houseAndLandConfig,
      unitTypes,
    },
    houseAndLand,
    whiteLabel,
  }: RootStateType) => ({
    projectName,
    lotFilter,
    prices,
    showPrice,
    houseAndLand,
    houseAndLandConfig,
    propertyConfig,
    aspects,
    filterWhiteLabel: mapWhiteLabelsWithDefaults(whiteLabel, true).filter,
    propertyTypeOptions: unitTypes,
  })
)(HouseAndLandFilterPopup)
